import { Trans } from "@lingui/react/macro"
import { Button, Divider, Menu } from "@mantine/core"
import {
  IconChevronDown,
  IconDownload,
  IconFileText,
  IconFileTypeHtml,
  IconFileTypePdf,
  IconMarkdown,
  IconMicrophone,
} from "@tabler/icons-react"
import { prefersM4aPlayback } from "../../lib/audio-format"
import type { IProps } from "./interfaces"

type TExportFormat = "txt" | "md" | "html" | "pdf"
type TExportContent = "summary" | "transcript"

/**
 * ExportMenu — meeting export dropdown.
 *
 * Lives on the meeting detail page. Offers the summary and the full transcript
 * each as their own format selection (txt / md / html / pdf), plus a download
 * of the mixed recording (Apple engines get the AAC/MP4 variant, everyone else
 * the WebM mix).
 */
export const ExportMenu = ({ meetingId, companyId, disabled = false, variant = "full" }: IProps): React.JSX.Element => {
  const transcriptOnly = variant === "transcript-only"
  const triggerDownload = (url: string): void => {
    // Navigate via a hidden anchor so the browser handles the
    // Content-Disposition: attachment response cleanly.
    const a = document.createElement("a")
    a.href = url
    a.rel = "noopener"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const exportUrl = (content: TExportContent, format: TExportFormat): string => {
    const params = new URLSearchParams({ format, content })
    return `/api/companies/${encodeURIComponent(companyId)}/meetings/${encodeURIComponent(meetingId)}/export?${params.toString()}`
  }

  const triggerExport = (content: TExportContent, format: TExportFormat): void => {
    triggerDownload(exportUrl(content, format))
  }

  const downloadRecording = (): void => {
    const base = `/api/companies/${encodeURIComponent(companyId)}/meetings/${encodeURIComponent(meetingId)}/recording/download`
    triggerDownload(prefersM4aPlayback() ? `${base}?format=m4a` : base)
  }

  const formatItems = (content: TExportContent): React.JSX.Element => (
    <>
      <Menu.Item leftSection={<IconFileTypePdf size={16} />} onClick={() => triggerExport(content, "pdf")}>
        {transcriptOnly ? <Trans>PDF</Trans> : <Trans>PDF (printable)</Trans>}
      </Menu.Item>
      {!transcriptOnly && (
        <Menu.Item leftSection={<IconFileTypeHtml size={16} />} onClick={() => triggerExport(content, "html")}>
          <Trans>HTML (web page)</Trans>
        </Menu.Item>
      )}
      <Menu.Item leftSection={<IconMarkdown size={16} />} onClick={() => triggerExport(content, "md")}>
        <Trans>Markdown</Trans>
      </Menu.Item>
      <Menu.Item leftSection={<IconFileText size={16} />} onClick={() => triggerExport(content, "txt")}>
        {transcriptOnly ? <Trans>Plain text</Trans> : <Trans>Plain text (email-friendly)</Trans>}
      </Menu.Item>
    </>
  )

  return (
    <Menu shadow="md" position="bottom-end" disabled={disabled}>
      <Menu.Target>
        <Button
          variant={transcriptOnly ? "filled" : "default"}
          color={transcriptOnly ? "brand" : undefined}
          leftSection={<IconDownload size={16} />}
          rightSection={transcriptOnly ? <IconChevronDown size={14} /> : undefined}
          disabled={disabled}
        >
          <Trans>Export</Trans>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {!transcriptOnly && (
          <>
            <Menu.Label>
              <Trans>Summary</Trans>
            </Menu.Label>
            {formatItems("summary")}
            <Divider />
          </>
        )}
        <Menu.Label>
          <Trans>Transcript</Trans>
        </Menu.Label>
        {formatItems("transcript")}
        <Divider />
        <Menu.Label>
          <Trans>Recording</Trans>
        </Menu.Label>
        <Menu.Item leftSection={<IconMicrophone size={16} />} onClick={downloadRecording}>
          {transcriptOnly ? <Trans>Audio</Trans> : <Trans>Download recording (mixed audio)</Trans>}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}
